import {
    Input,
    getBoolValue,
    getStringValue,
    setProperty,
    ValidationResults,
    ValidationError,
    IValidationError,
} from 'adaptivecards';
import { DatePicker, DayOfWeek } from '@uifabric/date-time/lib/DatePicker';
import ActionableMessageCardStrings from '../store/schema/ActionableMessageCardStrings';

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import styles from '../styles/cardStyles.scss';

export default class CustomDateInput extends Input {
    private datePickerElement: HTMLDivElement;
    private _min: string;
    private _max: string;
    private selectedDate: Date = null;

    public static strings: ActionableMessageCardStrings;

    public isRequired: boolean = false;
    public placeholder: string;

    private onSelectDate = (newDate: Date) => {
        if (newDate instanceof Date) {
            this.selectedDate = newDate;
        }
    };

    private dateNotValid = () => {
        return this.isRequired && this.selectedDate === null;
    };

    protected internalRender(): HTMLElement {
        this.datePickerElement = document.createElement('div');
        this.datePickerElement.className = styles.inputElementWrapper;
        const todayDate: Date = new Date(Date.now());
        const defaultDate: Date = this.defaultValue ? new Date(this.defaultValue) : null;
        const minDate: Date = this.min ? new Date(this.min) : null;
        const maxDate: Date = this.max ? new Date(this.max) : null;
        const placeholderValue = this.placeholder || CustomDateInput.strings.placeholder.dateInput;

        ReactDOM.render(
            <React.StrictMode>
                <DatePicker
                    strings={CustomDateInput.strings.calender}
                    isMonthPickerVisible={false}
                    showMonthPickerAsOverlay={true}
                    firstDayOfWeek={DayOfWeek.Sunday}
                    today={todayDate}
                    value={defaultDate}
                    minDate={minDate}
                    maxDate={maxDate}
                    isRequired={this.isRequired}
                    placeholder={placeholderValue}
                    ariaLabel={placeholderValue}
                    onSelectDate={this.onSelectDate}
                />
            </React.StrictMode>,
            this.datePickerElement
        );

        return this.datePickerElement;
    }

    public toJSON(): any {
        let result = super.toJSON();
        setProperty(result, 'min', this.min);
        setProperty(result, 'max', this.max);
        setProperty(result, 'isRequired', this.isRequired, false);
        setProperty(result, 'placeholder', this.placeholder);
        return result;
    }

    public parse(json: any, errors?: Array<IValidationError>) {
        super.parse(json, errors);
        this.min = getStringValue(json['min']);
        this.max = getStringValue(json['max']);
        this.isRequired = getBoolValue(json['isRequired'], this.isRequired);
        this.placeholder = getStringValue(json['placeholder'], this.placeholder);
        if (this.defaultValue) {
            this.selectedDate = new Date(this.defaultValue);
        }
    }

    public remove(): boolean {
        if (this.datePickerElement) {
            ReactDOM.unmountComponentAtNode(this.datePickerElement);
            this.datePickerElement = null;
        }

        return super.remove();
    }

    public internalValidateProperties(context: ValidationResults) {
        if (this.dateNotValid()) {
            context.addFailure(this, {
                error: ValidationError.InvalidPropertyValue,
                message: CustomDateInput.strings.warning.missingRequiredInput,
            });
        }
        return [];
    }

    public getJsonTypeName(): string {
        return 'Input.Date';
    }

    get min(): string {
        return this._min;
    }

    set min(value: string) {
        this._min = this.parseInputValue(value);
    }

    get max(): string {
        return this._max;
    }

    set max(value: string) {
        this._max = this.parseInputValue(value);
    }

    get value(): string {
        return this.selectedDate ? this.selectedDate.toISOString() : null;
    }
}
